import { useState, useEffect } from 'react';
import { monthlyExpensesApi, formatCurrency, MONTHS, MANUAL_EXPENSE_CATEGORIES } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Trash2, Save, Pencil, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export const MonthlyManualExpenses = ({ propertyId, onDataChange }) => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(MONTHS[new Date().getMonth()]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [lineItems, setLineItems] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  useEffect(() => {
    fetchExpenses();
  }, [propertyId, selectedMonth, selectedYear]);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const response = await monthlyExpensesApi.getAll(propertyId, selectedMonth, selectedYear);
      const data = response.data;
      
      if (data.length > 0) {
        setExpenses(data);
        setLineItems(data[0].expenses || []);
      } else {
        setExpenses([]);
        setLineItems([]);
      }
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to fetch expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const addLineItem = () => {
    setLineItems([
      ...lineItems,
      { description: '', amount: '', category: '' }
    ]);
    setHasChanges(true);
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value };
    setLineItems(updated);
    setHasChanges(true);
  };

  const removeLineItem = (index) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const saveExpenses = async () => {
    // Validate
    const validItems = lineItems.filter(item => 
      item.description && item.amount && item.category
    );

    if (validItems.length === 0 && lineItems.length > 0) {
      toast.error('Please fill in all fields for each expense');
      return;
    }

    setSaving(true);
    try {
      await monthlyExpensesApi.create({
        property_id: propertyId,
        month: selectedMonth,
        year: selectedYear,
        expenses: validItems.map(item => ({
          description: item.description,
          amount: parseFloat(item.amount),
          category: item.category
        }))
      });
      toast.success('Expenses saved');
      setHasChanges(false);
      fetchExpenses();
      onDataChange?.();
    } catch (error) {
      toast.error('Failed to save expenses');
    } finally {
      setSaving(false);
    }
  };

  const deleteAllExpenses = async () => {
    if (expenses.length === 0) return;

    try {
      await monthlyExpensesApi.delete(expenses[0].id);
      toast.success('Expenses deleted');
      setLineItems([]);
      setHasChanges(false);
      fetchExpenses();
      onDataChange?.();
    } catch (error) {
      toast.error('Failed to delete expenses');
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  const totalAmount = lineItems.reduce((sum, item) => {
    const amount = parseFloat(item.amount) || 0;
    return sum + amount;
  }, 0);

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-semibold font-display flex items-center gap-2">
          <Pencil className="w-5 h-5 text-slate-500" />
          Manual Expenses
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Month/Year Selector */}
        <div className="flex gap-3 items-end">
          <div>
            <Label className="text-xs text-slate-500">Month</Label>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-28 mt-1" data-testid="manual-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map(month => (
                  <SelectItem key={month} value={month}>{month}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Year</Label>
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-24 mt-1" data-testid="manual-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(year => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {expenses.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDeleteDialogOpen(true)}
              className="text-red-600 hover:text-red-700 ml-auto"
              data-testid="delete-all-expenses"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Clear All
            </Button>
          )}
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            {/* Expense Line Items */}
            <div className="space-y-3" data-testid="manual-expenses-list">
              {lineItems.length > 0 ? (
                <>
                  {/* Header */}
                  <div className="grid grid-cols-12 gap-2 text-xs font-medium text-slate-500">
                    <div className="col-span-4">Description</div>
                    <div className="col-span-3">Category</div>
                    <div className="col-span-3">Amount (£)</div>
                    <div className="col-span-2"></div>
                  </div>
                  
                  {lineItems.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <Input
                        placeholder="e.g., Boiler service"
                        value={item.description}
                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                        className="col-span-4"
                        data-testid={`expense-description-${index}`}
                      />
                      <Select
                        value={item.category}
                        onValueChange={(v) => updateLineItem(index, 'category', v)}
                      >
                        <SelectTrigger className="col-span-3" data-testid={`expense-category-${index}`}>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          {MANUAL_EXPENSE_CATEGORIES.map(cat => (
                            <SelectItem key={cat.value} value={cat.value}>
                              {cat.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        value={item.amount}
                        onChange={(e) => updateLineItem(index, 'amount', e.target.value)}
                        className="col-span-3"
                        data-testid={`expense-amount-${index}`}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLineItem(index)}
                        className="col-span-2 h-9 text-red-600 hover:text-red-700"
                        data-testid={`remove-expense-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}

                  {/* Total */}
                  <div className="flex justify-between items-center pt-3 border-t border-slate-200">
                    <span className="text-sm font-medium text-slate-700">Total</span>
                    <span className="text-lg font-bold text-slate-900">
                      {formatCurrency(totalAmount)}
                    </span>
                  </div>
                </>
              ) : (
                <div className="text-center py-6 text-slate-500">
                  <Pencil className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">No expenses for {selectedMonth} {selectedYear}</p>
                  <p className="text-xs mt-1">Add expenses not included in agent reports</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={addLineItem}
                className="flex-1"
                data-testid="add-expense-line"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Expense
              </Button>
              {lineItems.length > 0 && (
                <Button
                  onClick={saveExpenses}
                  disabled={!hasChanges || saving}
                  className="flex-1 bg-slate-900 hover:bg-slate-800"
                  data-testid="save-manual-expenses"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save
                </Button>
              )}
            </div>
          </>
        )}
      </CardContent>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear All Expenses?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete all manual expenses for {selectedMonth} {selectedYear}. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteAllExpenses} className="bg-red-600 hover:bg-red-700">
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default MonthlyManualExpenses;
